/**
 * The URL parameter used to force logging, e.g., `?log=true`
 */
const LOG_OVERRIDE_PARAM = 'log';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/*
 * Logger class that implements a singleton pattern to ensure only one instance exists.
 * It provides methods for logging messages at different levels (debug, info, warn, error).
 * It also checks for a URL parameter ?log=true to force logging behavior, allowing developers to see logs in production environments if needed.
 * This is useful for debugging issues that may not be reproducible in development.
 * It respects the environment settings, logging everything in development and only warnings and errors in production.
 * This class is designed to be used across the application, providing a consistent logging interface.
 * * Usage:
 * ```typescript
 * import { Logger, getLogger } from 'path/to/logger';
 * // Create a logger instance, typically in your main application file
 * const logger = Logger.createInstance(import.meta.env.PROD);
 * // In any other file:
 * const logger = getLogger();
 * // Use the logger instance to log messages
 * logger.debug('This is a debug message');
 * logger.info('This is an info message');
 * logger.warn('This is a warning message');
 * logger.error('This is an error message');
 *
 * Future work might include the ability to log to external services or files.
 * This will need some consideration for performance and security, especially in production environments.
 * We would also need to determine where and how to store these logs.
 */
export class Logger {
  private static instance: Logger | undefined;
  private readonly isLogForced: boolean = false;

  /**
   * The constructor is private to enforce the singleton pattern.
   * It checks for the existence of a URL parameter to override environment settings.
   */
  private constructor(
    private readonly isProduction: boolean,
    /** Whether to parse the URL for log override parameter. Currently only used to simplify testing. */
    parseUrl: boolean
  ) {
    if (parseUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      this.isLogForced = urlParams.get(LOG_OVERRIDE_PARAM) === 'true';
    }
    this.debug('Logger initialized. Forced logging:', this.isLogForced);
  }

  public static createInstance(isProduction: boolean): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(isProduction, true);
      return Logger.instance;
    } else {
      console.warn(
        'Logger instance already exists. Returning existing instance.'
      );
      return Logger.instance;
    }
  }

  /**
   * Provides access to the single logger instance.
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      console.log('Logger instance does not exist. Creating default instance.');
      Logger.instance = new Logger(false, false);
    }
    return Logger.instance;
  }

  /**
   * Determines if a message of a given level should be logged.
   * - Logs everything if forced by the URL parameter (`?log=true`).
   * - Logs everything in development mode (`import.meta.env.DEV`).
   * - Logs only warnings and errors in production mode.
   * @param level The log level of the message.
   * @returns {boolean}
   */
  private shouldLog(level: LogLevel): boolean {
    if (this.isLogForced) {
      return true;
    }

    if (!this.isProduction) {
      return true;
    }

    // In production, only log warnings and errors
    return level === 'WARN' || level === 'ERROR';
  }

  /**
   * Logs a debug message. Typically for detailed diagnostic information.
   * Only shown in development or when logging is forced.
   */
  public debug(...args: unknown[]): void {
    if (this.shouldLog('DEBUG')) {
      console.debug('[DEBUG]', ...args);
    }
  }

  /**
   * Logs an informational message.
   * Only shown in development or when logging is forced.
   */
  public info(...args: unknown[]): void {
    if (this.shouldLog('INFO')) {
      console.info('[INFO]', ...args);
    }
  }

  /**
   * Logs a warning message.
   * Shown in all environments.
   */
  public warn(...args: unknown[]): void {
    if (this.shouldLog('WARN')) {
      console.warn('[WARN]', ...args);
    }
  }

  /**
   * Logs an error message.
   * Shown in all environments.
   */
  public error(...args: unknown[]): void {
    if (this.shouldLog('ERROR')) {
      console.error('[ERROR]', ...args);
    }
  }
}

// Export a singleton instance for easy imports across the application
export const getLogger = (): Logger => Logger.getInstance();
